import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { personFileService } from '../services/api';
import { Building2, Mail, Phone, MapPin, User, Plus, X, RefreshCw, History, PenSquare, Search, FileText, ShieldCheck, Download, Upload, AlertTriangle } from 'lucide-react';

const COMPANY_TYPES = ['customer', 'carrier', 'vendor', 'partner'];
const COMPANY_DOC_TYPES = [
  ['coi', 'Certificate of Insurance (COI)'], ['w9', 'W-9'], ['operating_authority', 'Operating Authority'],
  ['mc_authority', 'MC Authority'], ['insurance', 'Insurance Policy'], ['contract', 'Contract / MSA'],
  ['rate_agreement', 'Rate Agreement'], ['credit_reference', 'Credit Reference'],
  ['tax_exempt', 'Tax Exemption'], ['nda', 'NDA'], ['other', 'Other'],
];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CORE_FIELDS = [
  ['name', 'Company Name'], ['legal_name', 'Legal Name'], ['email', 'Email'],
  ['phone', 'Phone'], ['city', 'City'], ['state', 'State'],
];

const CompanyFilePage = () => {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [newNote, setNewNote] = useState('');
  const [showAssoc, setShowAssoc] = useState(false);
  const [assocQ, setAssocQ] = useState('');
  const [assocResults, setAssocResults] = useState([]);
  const [editCore, setEditCore] = useState(false);
  const [cf, setCf] = useState({});
  const [showDoc, setShowDoc] = useState(false);
  const [docForm, setDocForm] = useState({ doc_type: 'coi', label: '', expiration_date: '', file: null });

  const load = useCallback(async () => {
    setLoading(true);
    try { setC(await personFileService.getCompany(id)); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed to load company'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const startEditCore = () => {
    setErr(''); setMsg('');
    setCf({
      name: c.name || '', legal_name: c.legal_name || '', company_type: c.company_type || 'customer',
      email: c.email || '', phone: c.phone || '', city: c.city || '', state: c.state || '',
    });
    setEditCore(true);
  };

  const saveCore = async () => {
    if (!cf.name || !cf.name.trim()) { setErr('Company name is required.'); return; }
    if (cf.email && cf.email.trim() && !EMAIL_RE.test(cf.email.trim())) { setErr('Please enter a valid email address.'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await personFileService.updateCompany(id, {
        name: cf.name.trim(), legal_name: cf.legal_name, company_type: cf.company_type,
        email: cf.email, phone: cf.phone, city: cf.city, state: cf.state,
      });
      setEditCore(false);
      await load();
      setMsg('Company details saved.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to save company details.'); }
    finally { setBusy(false); }
  };

  const uploadDoc = async () => {
    if (!docForm.file) { setErr('Choose a file to upload.'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('doc_type', docForm.doc_type);
      if (docForm.label) fd.append('label', docForm.label);
      if (docForm.expiration_date) fd.append('expiration_date', docForm.expiration_date);
      fd.append('file', docForm.file);
      await personFileService.companyAddDocument(id, fd);
      setShowDoc(false);
      setDocForm({ doc_type: 'coi', label: '', expiration_date: '', file: null });
      await load();
      setMsg('Document uploaded — marked RECEIVED (verify it separately when confirmed).');
      setTimeout(() => setMsg(''), 4000);
    } catch (e) { setErr(e?.response?.data?.detail || 'Upload failed.'); }
    finally { setBusy(false); }
  };

  const verifyDoc = async (docId) => {
    setBusy(true); setErr('');
    try { await personFileService.companyVerifyDocument(id, docId, 'verify', 'Verified from Company File'); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Verify failed.'); }
    finally { setBusy(false); }
  };

  const downloadDoc = async (docId) => {
    try { const r = await personFileService.companyDownloadDocument(id, docId); if (r.download_url) window.open(r.download_url, '_blank'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Download failed.'); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setBusy(true);
    try { await personFileService.updateCompany(id, { note: newNote.trim() }); setNewNote(''); await load(); }
    finally { setBusy(false); }
  };

  const searchPeople = async () => {
    const r = await personFileService.listPeople(assocQ);
    setAssocResults(r.people || []);
  };

  const associate = async (pid) => {
    setBusy(true);
    try { await personFileService.updateCompany(id, { add_contact_person_id: pid }); setShowAssoc(false); setAssocResults([]); setAssocQ(''); await load(); }
    finally { setBusy(false); }
  };

  const removeContact = async (pid) => {
    setBusy(true);
    try { await personFileService.updateCompany(id, { remove_contact_person_id: pid }); await load(); }
    finally { setBusy(false); }
  };

  if (loading) return <DashboardLayout><div className="p-6 flex justify-center"><RefreshCw className="w-8 h-8 animate-spin text-gray-400" /></div></DashboardLayout>;
  if (!c) return <DashboardLayout><div className="p-6"><div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{err || 'Company not found'}</div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="company-file-page">
        <Breadcrumb items={[{ label: 'People & Companies', path: '/admin/people' }, { label: c.name }]} />

        {msg && <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700" data-testid="company-save-msg">{msg}</div>}
        {err && editCore && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700" data-testid="company-save-err">{err}</div>}

        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6" data-testid="company-header">
          {!editCore ? (
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-14 h-14 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0"><Building2 className="w-7 h-7 text-indigo-900" /></div>
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold text-gray-900 break-words" data-testid="company-name">{c.name}</h1>
                  {c.legal_name && <p className="text-sm text-gray-500 break-words">{c.legal_name}</p>}
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1 min-w-0"><Mail className="w-4 h-4 flex-shrink-0" /><span className="truncate">{c.email || '—'}</span></span>
                    <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{c.phone || '—'}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{c.city || '—'}, {c.state || '—'}</span>
                  </div>
                  <span className="inline-block mt-3 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">{c.company_type}</span>
                </div>
              </div>
              <button onClick={startEditCore} data-testid="edit-company-btn"
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 shrink-0">
                <PenSquare className="w-4 h-4 text-gray-500" /> Edit
              </button>
            </div>
          ) : (
            <div data-testid="company-edit-form">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-900" /> Edit Company Details</h2>
                <div className="flex gap-2">
                  <button onClick={saveCore} disabled={busy} data-testid="save-company-btn"
                    className="px-4 py-1.5 rounded-lg bg-indigo-900 text-white text-sm font-medium hover:bg-indigo-800 disabled:opacity-40">{busy ? 'Saving…' : 'Save'}</button>
                  <button onClick={() => { setEditCore(false); setErr(''); }} data-testid="cancel-company-btn"
                    className="px-4 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {CORE_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <label className="text-xs text-gray-500 block mb-1">{label}{k === 'name' ? ' *' : ''}</label>
                    <input value={cf[k] || ''} onChange={(e) => setCf({ ...cf, [k]: e.target.value })} data-testid={`company-field-${k}`}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Type / Classification</label>
                  <select value={cf.company_type || 'customer'} onChange={(e) => setCf({ ...cf, company_type: e.target.value })} data-testid="company-field-company_type"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white">
                    {COMPANY_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Contacts */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6" data-testid="company-contacts">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><User className="w-5 h-5 text-indigo-900" /> Associated People</h2>
                <button onClick={() => setShowAssoc(true)} data-testid="add-contact-btn"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-800 bg-indigo-50 rounded-lg hover:bg-indigo-100">
                  <Plus className="w-4 h-4" /> Associate Person
                </button>
              </div>
              {(!c.contacts || c.contacts.length === 0) ? <p className="text-sm text-gray-500">No associated people yet.</p> : (
                <div className="space-y-2">
                  {c.contacts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 gap-3" data-testid={`contact-row-${p.id}`}>
                      <Link to={`/admin/person/${p.id}`} className="min-w-0 flex-1 hover:underline">
                        <p className="text-sm font-medium text-gray-800 truncate">{p.full_name}</p>
                        <p className="text-xs text-gray-400 truncate">{p.email} · {p.status}</p>
                      </Link>
                      <button onClick={() => removeContact(p.id)} data-testid={`remove-contact-${p.id}`}
                        className="text-xs text-red-600 hover:underline flex-shrink-0">Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Documents (RECEIVED ≠ VERIFIED) */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6" data-testid="company-documents">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-900" /> Documents</h2>
                <button onClick={() => { setShowDoc(true); setErr(''); }} data-testid="company-add-doc-btn"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-800 bg-indigo-50 rounded-lg hover:bg-indigo-100">
                  <Upload className="w-4 h-4" /> Add Document
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">Uploading marks a document <strong>RECEIVED</strong>. Verification is a separate authorized step (RECEIVED ≠ VERIFIED).</p>
              {(!c.documents || c.documents.length === 0) ? <p className="text-sm text-gray-500">No documents yet.</p> : (
                <div className="space-y-2">
                  {c.documents.map((d) => (
                    <div key={d.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between border border-gray-100 rounded-lg px-3 py-2 gap-2 sm:gap-3" data-testid={`company-doc-${d.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{d.label || d.doc_type} <span className="text-xs text-gray-400">· {d.filename}</span></p>
                        <p className="text-xs text-gray-400">
                          Received {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : '—'}
                          {d.verified && d.verified_by ? ` · Verified by ${d.verified_by} ${d.verified_at ? new Date(d.verified_at).toLocaleDateString() : ''}` : ''}
                          {d.expiration_date ? ` · Expires ${new Date(d.expiration_date).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0">
                        {d.expired && <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 flex items-center gap-1" title="Expired"><AlertTriangle className="w-3.5 h-3.5" /> EXPIRED</span>}
                        {d.verified
                          ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 flex items-center gap-1" data-testid={`company-doc-badge-${d.id}`}><ShieldCheck className="w-3.5 h-3.5" /> VERIFIED</span>
                          : <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-testid={`company-doc-badge-${d.id}`}>RECEIVED</span>}
                        <button onClick={() => downloadDoc(d.id)} data-testid={`company-doc-download-${d.id}`}
                          className="text-gray-500 hover:text-indigo-900" title="Download"><Download className="w-4 h-4" /></button>
                        {!d.verified && (
                          <button onClick={() => verifyDoc(d.id)} disabled={busy} data-testid={`company-doc-verify-${d.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-800 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-40">
                            <ShieldCheck className="w-3.5 h-3.5" /> Verify</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6" data-testid="company-notes">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><PenSquare className="w-5 h-5 text-indigo-900" /> Notes</h2>
              <div className="flex gap-2 mb-4">
                <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note…" data-testid="company-note-input"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
                <button onClick={addNote} disabled={busy || !newNote.trim()} data-testid="company-note-add-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-900 text-white hover:bg-indigo-800 disabled:opacity-40">Add</button>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {(c.notes || []).slice().reverse().map((n, i) => (
                  <div key={i} className="text-sm border-l-2 border-gray-200 pl-3 py-1">
                    <p className="text-gray-800">{n.note}</p>
                    <p className="text-xs text-gray-400">{n.author} · {n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}</p>
                  </div>
                ))}
                {(!c.notes || c.notes.length === 0) && <p className="text-sm text-gray-500">No notes yet.</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 min-w-0" data-testid="company-audit">
            <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-indigo-900" /> Activity</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(c.audit_trail || []).map((a, i) => (
                <div key={i} className="text-xs border-l-2 border-gray-200 pl-3 py-1">
                  <p className="font-medium text-gray-700">{a.action}</p>
                  <p className="text-gray-400">{a.details?.actor_email} · {a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</p>
                </div>
              ))}
              {(!c.audit_trail || c.audit_trail.length === 0) && <p className="text-sm text-gray-500">No activity yet.</p>}
            </div>
          </div>
        </div>
      </div>

      {showDoc && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="company-doc-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Company Document</h3>
              <button onClick={() => setShowDoc(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {err && <div className="mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{err}</div>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Document Type</label>
                <select value={docForm.doc_type} onChange={(e) => setDocForm({ ...docForm, doc_type: e.target.value })} data-testid="company-doc-type"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                  {COMPANY_DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Label (optional)</label>
                <input value={docForm.label} onChange={(e) => setDocForm({ ...docForm, label: e.target.value })} data-testid="company-doc-label"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. 2026 COI" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Expiration date (optional)</label>
                <input type="date" value={docForm.expiration_date} onChange={(e) => setDocForm({ ...docForm, expiration_date: e.target.value })} data-testid="company-doc-exp"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">File</label>
                <input type="file" onChange={(e) => setDocForm({ ...docForm, file: e.target.files[0] })} data-testid="company-doc-file"
                  className="w-full text-sm" />
              </div>
              <button onClick={uploadDoc} disabled={busy || !docForm.file} data-testid="company-doc-upload-btn"
                className="w-full py-2 rounded-lg bg-indigo-900 text-white text-sm font-medium hover:bg-indigo-800 disabled:opacity-40">
                {busy ? 'Uploading…' : 'Upload (mark RECEIVED)'}</button>
            </div>
          </div>
        </div>
      )}

      {showAssoc && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="assoc-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Associate a Person</h3>
              <button onClick={() => setShowAssoc(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="flex gap-2 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input value={assocQ} onChange={(e) => setAssocQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchPeople()}
                  placeholder="Search people…" data-testid="assoc-search"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <button onClick={searchPeople} className="px-3 py-2 bg-gray-100 rounded-lg text-sm">Search</button>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {assocResults.map((p) => (
                <button key={p.id} onClick={() => associate(p.id)} data-testid={`assoc-person-${p.id}`}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-indigo-50 text-sm">
                  <span className="font-medium text-gray-800">{p.full_name}</span>
                  <span className="text-xs text-gray-400 ml-2">{p.email}</span>
                </button>
              ))}
              {assocResults.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Search to find people to associate.</p>}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CompanyFilePage;
